import { ReactNode, useState } from "react";
import AppSidebar from "./AppSidebar";
import MobileBottomNav from "./MobileBottomNav";
import { useIsMobile } from "@/hooks/use-mobile";
import { Menu, X, Bell, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { toast } from "sonner";

function MobilePushGate({ onSkip, onSubscribed }: { onSkip: () => void; onSubscribed: () => void }) {
  const { isSupported, permission, loading, subscribe, debugInfo } = usePushNotifications();

  const handleEnable = async () => {
    const result = await subscribe();
    if (result.success) {
      toast.success("Bildirimler açıldı!");
      onSubscribed();
    } else {
      toast.error(result.reason || "Bildirim aboneliği başarısız oldu.");
    }
  };

  const canEnable = isSupported && permission !== "denied";

  return (
    <div className="flex flex-col items-center justify-center h-screen bg-background px-6 text-center gap-6">
      <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
        <Bell className="h-8 w-8 text-primary" />
      </div>
      <h2 className="text-xl font-bold text-foreground">Bildirimleri Açın</h2>
      <p className="text-sm text-muted-foreground max-w-xs">
        İş emirleri ve güncellemelerden anında haberdar olmak için push bildirimlerini etkinleştirin.
      </p>
      {!isSupported && (
        <p className="text-xs text-destructive max-w-xs">
          Bu cihaz/tarayıcı push bildirimleri desteklemiyor. Uygulamayı ana ekrana ekleyip tekrar deneyin (iOS 16.4+ gereklidir).
          {debugInfo && <span className="block mt-1 font-mono">{debugInfo}</span>}
        </p>
      )}
      {permission === "denied" && isSupported && (
        <p className="text-xs text-destructive max-w-xs">
          Bildirim izni reddedildi. Cihaz Ayarları → Bildirimler → Saha Servis → İzin Ver yaparak izni sıfırlayın.
        </p>
      )}
      {canEnable ? (
        <Button onClick={handleEnable} disabled={loading} size="lg" className="w-full max-w-xs">
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          <Bell className="mr-2 h-4 w-4" />
          Bildirimleri Etkinleştir
        </Button>
      ) : (
        <Button onClick={onSkip} variant="outline" size="lg" className="w-full max-w-xs">
          Bildirimsiz Devam Et
        </Button>
      )}
    </div>
  );
}

export default function AppLayout({ children }: { children: ReactNode }) {
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pushSkipped, setPushSkipped] = useState(false);
  const { isSubscribed } = usePushNotifications();

  if (isMobile && !isSubscribed && !pushSkipped) {
    return <MobilePushGate onSkip={() => setPushSkipped(true)} onSubscribed={() => setPushSkipped(true)} />;
  }

  if (isMobile) {
    return (
      <div className="flex flex-col h-screen overflow-hidden">
        <header className="flex items-center justify-between px-4 py-3 border-b border-border bg-background shrink-0">
          <h1 className="text-sm font-bold text-primary">Saha Servis</h1>
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </header>

        {sidebarOpen && (
          <>
            <div className="fixed inset-0 z-40 bg-black/50" onClick={() => setSidebarOpen(false)} />
            <div className="fixed top-0 left-0 z-50 h-full w-64">
              <AppSidebar onNavigate={() => setSidebarOpen(false)} />
            </div>
          </>
        )}

        <main className="flex-1 overflow-auto bg-background pb-16">{children}</main>
        <MobileBottomNav />
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden">
      <AppSidebar />
      <main className="flex-1 overflow-auto bg-background">{children}</main>
    </div>
  );
}
